import { type FC } from 'react';
import QrDisplayer from 'libraries/qr-code';
import { FormattedMessage } from 'react-intl';
import { MRT_ColumnDef } from 'material-react-table';
import { Eye } from 'lucide';

// MUI
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

// common
import Icon from 'common/components/Icon';
import MuiTable from 'common/components/MuiTable';
import MyLink from 'common/components/MyLink';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import { KIOS_STORAGE } from 'api/API_App';
import { LINKING_STATUS } from 'common/constants';
import { formatDateWithoutTimezone } from 'common/utils';
import { ROUTES_PATH } from 'layout/constants';

// screen
import { SupplierListProps } from 'modules/suppliers/types';
import { Supplier } from 'modules/suppliers/services/useProcessingFoodSuppliers';
import { saveSupplierExtras } from 'modules/suppliers/helpers/supplierExtras';
import KiosImage from 'modules/quick-search/components/KiosImage';

/** Ảnh KIOS có lúc là URL đầy đủ, có lúc là path tương đối trong storage. */
const storageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  const trimmed = path.replace(/^\/+/, '').replace(/^storage\//, '');
  return `${(KIOS_STORAGE || '').replace(/\/+$/, '')}/${trimmed}`;
};

interface SupplierTableProps extends SupplierListProps {
  /**
   *  nếu trạng thái nhà cung cấp là đang tạm dừng thì ẩn cột mã QR và cột chức năng
   */
  hideColumns?: boolean;
}

const SupplierTable: FC<SupplierTableProps> = (props) => {
  const {
    isLoading,
    params,
    suppliers = [],
    totalCount,
    tableProps,
    tab,
    hideColumns = false,
  } = props;

  const { sorting, onSortingChange, onPageChange, onRowsPerPageChange } = tableProps;

  const columns: MRT_ColumnDef<Supplier>[] = [
    {
      id: 'rowNumber',
      header: 'STT',
      Header: () => <FormattedMessage id="stt" />,
      Cell: ({ row }) => {
        const rowIndex = row.index + 1; // row.index is 0-based
        const offset = (params.pageIndex - 1) * params.pageSize;
        return offset + rowIndex;
      },
      size: 60,
      enableSorting: false,
    },
  ];

  if (!hideColumns) {
    columns.push({
      accessorKey: 'qrCode',
      header: 'qrCode',
      Header: () => <FormattedMessage id="supplier.generalDetails.qrCode" />,
      size: 110,
      minSize: 110,
      Cell: ({ renderedCellValue }) => {
        return <QrDisplayer value={renderedCellValue?.toString() || ''} size={70} level="M" />;
      },
    });
  }

  columns.push(
    {
      accessorKey: 'name',
      header: 'name',
      Header: () => <FormattedMessage id="supplier.generalDetails.supplierName" />,
    },
    {
      accessorKey: 'address',
      header: 'address',
      Header: () => <FormattedMessage id="supplier.generalDetails.address" />,
    },
    {
      accessorKey: 'phone',
      header: 'phone',
      Header: () => <FormattedMessage id="supplier.generalDetails.phone" />,
    },
    {
      accessorKey: 'businessRegistration',
      header: 'businessRegistration',
      Header: () => (
        <FormattedMessage id="supplier.generalDetails.businessRegistrationCertificate" />
      ),
      size: 250,
      Cell: ({
        row: {
          original: { businessRegistration },
        },
      }) => {
        const reg =
          typeof businessRegistration === 'object' && businessRegistration !== null
            ? businessRegistration
            : {};
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography>{`${reg.registrationNumber || ''}`}</Typography>
            <Typography>{reg.publishedAt ? formatDateWithoutTimezone(String(reg.publishedAt)) : ''}</Typography>
            <Typography>{`${reg.issuedBy || ''}`}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'commodityGroup',
      accessorKey: 'classificationGroups',
      header: 'goodsTypes',
      Header: () => <FormattedMessage id="supplier.generalDetails.goodsTypes" />,
      size: 200,
      accessorFn: (row) => row.classificationGroups?.join(', ') || '',
      Cell: ({
        row: {
          original: { classificationGroups: c = [] },
        },
      }) => {
        const joinedGoodsTypes = c.join(', ');
        const tooltipTitle = c.length > 5 ? joinedGoodsTypes : undefined;

        return (
          <Tooltip title={tooltipTitle}>
            <Typography>{joinedGoodsTypes}</Typography>
          </Tooltip>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: 'image',
      header: 'hsnlImage',
      Header: () => <FormattedMessage id="supplier.generalDetails.hsnlImage" />,
      size: 160,
      minSize: 160,
      enableSorting: false,
      Cell: ({
        row: {
          original: { image, name },
        },
      }) => (
        <KiosImage
          src={storageUrl(image)}
          alt={name}
          fallbackIconSize={28}
          sx={{
            width: 80,
            height: 80,
            objectFit: 'cover',
            borderRadius: 1,
            display: 'block',
            margin: '0 auto',
          }}
        />
      ),
    },
    {
      accessorKey: 'description',
      header: 'productDescription',
      Header: () => <FormattedMessage id="supplier.generalDetails.productDescription" />,
      size: 240,
      enableSorting: false,
      Cell: ({
        row: {
          original: { description },
        },
      }) => {
        const text = description || '';
        return (
          <Tooltip title={text.length > 80 ? text : undefined}>
            <Typography
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {text}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'status',
      Header: () => <FormattedMessage id="status" />,
      Cell: ({ renderedCellValue: val }) => {
        const s = LINKING_STATUS.find((s) => s.id === val)?.name || 'N/A';
        return <FormattedMessage id={s} />;
      },
    }
  );

  return (
    <MuiTable<Supplier>
      enableRowNumbers={false}
      data={suppliers}
      manualSorting
      enableSortingRemoval={false}
      state={{ isLoading, sorting }}
      onSortingChange={onSortingChange}
      paginationProps={{
        count: totalCount || 0,
        page: params.pageIndex,
        rowsPerPage: params.pageSize,
        onPageChange,
        onRowsPerPageChange,
      }}
      columns={columns}
      enableRowActions={!hideColumns}
      renderRowActions={({ row: { original } }) => {
        const { status, operationCode } = original;
        return [
          status !== 'paused' && (
            <MyLink
              to={ROUTES_PATH.supplier.detail(
                tab,
                params.filters.customerOperationCode,
                operationCode
              )}
              // Ảnh + mô tả sản phẩm chỉ có ở API danh sách, API chi tiết KHÔNG trả — lưu lại để
              // khối "Thông tin cơ bản HSNL" ở trang chi tiết có cái mà hiện.
              onClick={() => saveSupplierExtras(original)}
            >
              <IconButtonTooltip
                aria-label="Xem nhanh"
                tooltipProps={{ title: <FormattedMessage id="quickView" /> }}
              >
                <Icon icon={Eye} />
              </IconButtonTooltip>
            </MyLink>
          ),
        ];
      }}
    />
  );
};

export default SupplierTable;
